import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import {
    AangeverBurgerlijkeStaat,
    Aangifte, BokkzAlgDataService,
    BokkzIbDataService,
    IncomeTaxEntity,
    Periode,
    Relatiegegevens
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {filter, Subscription} from 'rxjs';
import {Event, NavigationEnd, Router} from '@angular/router';
import { SharedUiTooltipModule } from '@snelstart/ui-tooltip';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { CommonModule } from '@angular/common';
import { SharedUiAlertModule } from '@snelstart/ui-alert';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-relatiegegevens',
  templateUrl: './relatiegegevens.component.html',
  styleUrls: ['./relatiegegevens.component.scss'],
    imports: [
        SharedUiTooltipModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        CommonModule,
        SharedUiAlertModule
    ]
})
export class RelatiegegevensComponent implements OnInit, OnDestroy {
    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private isSaving = false;
    public clientId!: string;

    @Input() public id!: string;
    public incomeTaxEntity = {} as IncomeTaxEntity;

    private focusedElementId: string | null = null;

    public showSpinner = true;

    public relatieGegevens = {} as FormGroup;

    public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
        {value: true, label: 'Ja, heel jaar fiscaal partner'},
    ];

    public optionsJaNeeVoorwaarden: SelectFormControlItem<boolean | undefined>[] = [
        {value: true, label: 'Ja, wel van toepassing'},
        {value: false, label: 'Nee, niet van toepassing'},
    ];

    public optiesRelaties: SelectFormControlItem<boolean | undefined>[] = [
        {value: undefined, label: "--- selecteer ---"},
        {
            value: true,
            label: 'Aangever is een deel van het jaar gehuwd.',
        },
        {
            value: true,
            label:
                'Aangever en partner hebben samen een notarieel samenlevingscontract.',
        },
        {
            value: true,
            label: 'Aangever en partner hebben samen een kind.',
        },
        {
            value: true,
            label: 'De ene partner erkent een kind van de ander.',
        },
        {
            value: true,
            label:
                'Aangever en partner zijn samen eigenaar van een woning (hoofdverblijf).',
        },
        {
            value: true,
            label:
                'Er woont een minderjarig kind van een van beiden woont op woonadres.',
        },
        {
            value: true,
            label:
                'Aangever en partner waren vorig aangiftejaar samen fiscaal partners.',
        },
        {
            value: true,
            label: 'Aangever en partner zijn partners voor een pensioenregeling.',
        },
        {
            value: false,
            label: 'Geen van voorgaande situaties is van toepassing.',
        },
    ];

    constructor(
        public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService,
        private router: Router,
        private fb: FormBuilder,
    ) {
        this.relatieGegevens = this.fb.group({
            relaties: this.fb.array<Relatiegegevens>([] as Relatiegegevens[]),
        });
    }

    get relaties(): FormArray {
        return this.relatieGegevens.get('relaties') as FormArray;
    }

    addRijRelatiegegevens(): void {
        this.relaties.push(this.createRelatieGegevensFormGroup());
    }

    createRelatieGegevensFormGroup(relatiegegevens?: Relatiegegevens): FormGroup {
        return this.fb.group({
            omschrijving: relatiegegevens ? relatiegegevens.omschrijving : '',
            datumOverlijdenPartner: relatiegegevens ? relatiegegevens.datumOverlijdenPartner : (null as unknown as Date),
            datumScheiding: relatiegegevens ? relatiegegevens.datumScheiding : (null as unknown as Date),
            datumTrouwen: relatiegegevens ? relatiegegevens.datumTrouwen : (null as unknown as Date),
            periodeIngeschrevenOpZelfdeAdres: this.fb.group<Periode>({
                begindatum: relatiegegevens ? relatiegegevens.periodeIngeschrevenOpZelfdeAdres?.begindatum : (null as unknown as Date),
                einddatum: relatiegegevens ? relatiegegevens.periodeIngeschrevenOpZelfdeAdres?.einddatum : (null as unknown as Date)
            }),
            kiestVoorHeleJaarPartnerschap: relatiegegevens ? relatiegegevens.kiestVoorHeleJaarPartnerschap : (undefined as unknown as boolean),
            voldaanAanFiscaalPartnerschapVoorwaarden: relatiegegevens ? relatiegegevens.voldaanAanFiscaalPartnerschapVoorwaarden : (undefined as unknown as boolean),
        } as Relatiegegevens);
    }


    public ngOnInit(): void {
        this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                const aangifte = incomeTaxEntity.aangifte as Aangifte;
                this.relatieGegevens.patchValue(aangifte.aangever!.relatiegegevens!, {
                    emitEvent: false,
                });
                this.updateForm();
            }
        });
        this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
            this.clientId = client.sstRelationId!;
        })
        this.routerSubscription = this.router.events.pipe(filter(
            (event: Event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.handleUrlChange(event.urlAfterRedirects);
        });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe();
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.relatieGegevens.dirty;
    }

    private updateForm(): void {
        const relatiesLijst = this.incomeTaxEntity.aangifte?.aangever?.relatiegegevens || [];
        const formArray = this.relatieGegevens.get('relaties') as FormArray;
        const focusedControl = this.focusedElementId ? (document.getElementById(this.focusedElementId) as HTMLElement) : null;

        while (formArray.length > relatiesLijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        for (let i = 0; i < relatiesLijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(relatiesLijst[i]);
            } else {
                const formGroup = this.createRelatieGegevensFormGroup(relatiesLijst[i]);
                formArray.push(formGroup);
            }
        }

        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {
        this.relatieGegevens.updateValueAndValidity();
        setTimeout(() => {
            this.incomeTaxEntity.aangifte = {
                ...this.incomeTaxEntity.aangifte,
                aangever: {
                    ...this.incomeTaxEntity.aangifte?.aangever,
                    relatiegegevens: this.relatieGegevens.value.relaties,
                },
                partnerAangifte: {
                    ...this.incomeTaxEntity.aangifte?.partnerAangifte,
                    aangifteJaar: this.incomeTaxEntity.aangifte?.aangifteJaar,
                    aangever: {
                        ...this.incomeTaxEntity.aangifte?.partnerAangifte?.aangever,
                        relatiegegevens: this.relatieGegevens.value.relaties,
                    },
                }
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        }, 0);
    }

    protected readonly AangeverBurgerlijkeStaat = AangeverBurgerlijkeStaat;
}
